import React, { useState } from 'react';
import api from '../../utils/axios';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Checkbox } from '../ui/checkbox';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../ui/dialog';
import { toast } from 'sonner';
import { SinVencimientoCheck } from './SinVencimientoCheck';

const hoy = () => new Date().toLocaleDateString('en-CA', { timeZone: 'America/Argentina/Buenos_Aires' });

const AlcanceSelector = ({ alcance, setAlcance, monitores }) => {
  const todos = alcance === 'TODOS';
  const toggle = (id) => setAlcance(alcance.includes(id) ? alcance.filter((x) => x !== id) : [...alcance, id]);
  return (
    <div className="space-y-2">
      <Label className="text-slate-700">Alcance *</Label>
      <div className="flex gap-2">
        <Button type="button" size="sm" variant={todos ? 'default' : 'outline'} onClick={() => setAlcance('TODOS')} data-testid="lb-alcance-todos">
          Todos los monitores
        </Button>
        <Button type="button" size="sm" variant={todos ? 'outline' : 'default'} onClick={() => todos && setAlcance([])} data-testid="lb-alcance-elegir">
          Elegir monitores
        </Button>
      </div>
      {!todos && (
        <div className="max-h-44 overflow-y-auto border border-slate-200 rounded-md p-2 space-y-1" data-testid="lb-alcance-lista">
          {monitores.map((m) => (
            <label key={m.id} className="flex items-center gap-2 text-sm text-slate-700 cursor-pointer">
              <Checkbox checked={alcance.includes(m.id)} onCheckedChange={() => toggle(m.id)} data-testid={`lb-monitor-${m.id}`} />
              {m.nombre}
            </label>
          ))}
        </div>
      )}
    </div>
  );
};

// Alta o edición de una exclusión de lista blanca para un CUIT (Parte F7).
export const ListaBlancaDialog = ({ cuit, entrada, monitores, onClose, onDone }) => {
  const [alcance, setAlcance] = useState(entrada?.alcance || 'TODOS');
  const [motivo, setMotivo] = useState(entrada?.motivo || '');
  const [fecha, setFecha] = useState(entrada?.fecha_vencimiento || '');
  const [sinVenc, setSinVenc] = useState(!!entrada?.sin_vencimiento);
  const [enviando, setEnviando] = useState(false);

  const guardar = async () => {
    if (!motivo.trim()) return toast.error('El motivo es obligatorio');
    if (alcance !== 'TODOS' && alcance.length === 0) return toast.error('Elegí al menos un monitor');
    if (!sinVenc && !fecha) return toast.error('La fecha de vencimiento es obligatoria');
    setEnviando(true);
    const body = { cuit, alcance, motivo: motivo.trim(), fecha_vencimiento: sinVenc ? null : fecha, sin_vencimiento: sinVenc };
    try {
      if (entrada) await api.put(`/account-lists/lista-blanca/${entrada.id}`, body);
      else await api.post('/account-lists/lista-blanca', body);
      toast.success(entrada ? 'Exclusión actualizada' : 'Cliente agregado a la lista blanca');
      onDone();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'No se pudo guardar');
    } finally {
      setEnviando(false);
    }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent style={{ backgroundColor: '#ffffff' }} data-testid="lb-dialog">
        <DialogHeader>
          <DialogTitle className="text-slate-900">{entrada ? 'Editar exclusión' : 'Agregar a lista blanca'}</DialogTitle>
          <DialogDescription className="text-slate-600">
            El CUIT <span className="font-mono">{cuit}</span> deja de aparecer en los monitores elegidos mientras la exclusión esté vigente.
            Las alertas de CUIT vigilado y las reglas de bloqueo del monitor en vivo no se excluyen nunca.
          </DialogDescription>
        </DialogHeader>
        <AlcanceSelector alcance={alcance} setAlcance={setAlcance} monitores={monitores} />
        <div className="space-y-1.5">
          <Label className="text-slate-700">Motivo *</Label>
          <Textarea value={motivo} onChange={(e) => setMotivo(e.target.value)} rows={2} style={{ backgroundColor: '#ffffff' }} data-testid="lb-motivo" />
        </div>
        <div className="space-y-1.5">
          <Label className="text-slate-700">Fecha de vencimiento *</Label>
          <Input type="date" min={hoy()} value={fecha || ''} onChange={(e) => setFecha(e.target.value)} disabled={sinVenc}
            style={{ backgroundColor: '#ffffff' }} data-testid="lb-fecha" />
          <SinVencimientoCheck checked={sinVenc} onChange={setSinVenc} testid="lb-sin-vencimiento" />
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} data-testid="lb-cancelar">Cancelar</Button>
          <Button onClick={guardar} disabled={enviando} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="lb-guardar">
            {enviando ? 'Guardando…' : 'Guardar'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
